// src/expenseslist.js
import React from 'react';

const hasReceiptForExpense = (exp) => {
  const url =
    exp?.attachment_url ||
    exp?.attachmentUrl ||
    exp?.receipt_url ||
    exp?.receiptUrl ||
    exp?.attachment?.url ||
    null;

  return !!url || !!exp?.attachment?.id;
};

const getReceiptKind = (exp) => {
  if (!hasReceiptForExpense(exp)) return 'none';

  const mime = String(
    exp?.attachment_mime ||
    exp?.attachmentMime ||
    exp?.attachment?.type ||
    ''
  ).toLowerCase();
  const fileReference = String(
    exp?.attachment_original_name ||
    exp?.attachmentOriginalName ||
    exp?.attachment?.name ||
    exp?.attachment_url ||
    exp?.attachmentUrl ||
    exp?.receipt_url ||
    exp?.receiptUrl ||
    exp?.attachment?.url ||
    ''
  ).toLowerCase().split(/[?#]/)[0];

  if (mime === 'application/pdf' || fileReference.endsWith('.pdf')) return 'pdf';
  if (mime.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif)$/.test(fileReference)) return 'image';
  return 'attachment';
};

const ReceiptStatusGraphic = ({ kind, onOpen }) => {
  const clickable = kind !== 'none';
  const label = kind === 'pdf'
    ? 'Open PDF receipt'
    : kind === 'image'
      ? 'Open image receipt'
      : kind === 'attachment'
        ? 'Open attached receipt'
        : 'No receipt attached';
  const sharedProps = clickable ? {
    type: 'button',
    onClick: onOpen,
    title: label,
    'aria-label': label
  } : {
    role: 'img',
    title: label,
    'aria-label': label
  };
  const Element = clickable ? 'button' : 'div';

  return (
    <Element
      {...sharedProps}
      className={`jfn-exp-receipt-indicator jfn-exp-receipt-indicator--${kind}`}
    >
      <span className="jfn-exp-receipt-paper" aria-hidden="true">
        {kind === 'image' ? (
          <>
            <span className="jfn-exp-receipt-word">RECEIPT</span>
            <span className="jfn-exp-receipt-line" />
            <span className="jfn-exp-receipt-line jfn-exp-receipt-line--short" />
            <span className="jfn-exp-receipt-line" />
          </>
        ) : null}
        {kind === 'pdf' ? <span className="jfn-exp-pdf-word">PDF</span> : null}
        {kind === 'attachment' ? <span className="jfn-exp-attachment-mark">📎</span> : null}
        {kind === 'none' ? <span className="jfn-exp-no-receipt-face">✕</span> : null}
      </span>
    </Element>
  );
};

const ExpensesList = ({
  styles,
  isMobile,
  loading,
  expenses,
  money,
  toDisplayDate,
  editExpense,
  deleteExpense,
  openAttachment,
  viewExpense
}) => {
  const cardStyle = {
    background: '#ffffff',
    border: '8px solid black',
    padding: '14px',
    marginBottom: 0,
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap'
  };

  const leftStyle = {
    minWidth: isMobile ? 0 : '260px',
    flex: '1 1 420px'
  };

  const vendorStyle = {
    fontWeight: 900,
    textTransform: 'uppercase',
    fontSize: '22px',
    lineHeight: 1.1,
    minWidth: 0,
    whiteSpace: 'normal',
    overflowWrap: 'normal',
    wordBreak: 'normal'
  };

  const metaStyle = {
    fontWeight: 900,
    marginTop: '6px',
    overflowWrap: 'normal',
    wordBreak: 'normal'
  };

  const btnRow = {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center'
  };

  const mobileActionRow = {
    display: 'flex',
    gap: '6px',
    alignItems: 'stretch',
    width: '100%'
  };

  const mobileButton = (buttonStyle) => ({
    ...buttonStyle,
    padding: '10px',
    flex: '1 1 0',
    minWidth: 0,
    whiteSpace: 'nowrap'
  });

  return (
    <div>
      <style>{`
        .jfn-expense-list-row {
          --receipt-indicator-size: 60px;
          display: grid;
          grid-template-columns: var(--receipt-indicator-size) minmax(0, 1fr);
          align-items: center;
          gap: 10px;
          margin-bottom: 14px;
          width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        .jfn-expense-list-card {
          min-width: 0;
          width: 100%;
          box-sizing: border-box;
        }
        .jfn-exp-receipt-indicator {
          width: var(--receipt-indicator-size);
          height: 70px;
          min-width: 0;
          padding: 0;
          border: 0;
          background: transparent;
          display: flex;
          align-items: center;
          justify-content: center;
          justify-self: center;
          align-self: center;
          box-sizing: border-box;
        }
        button.jfn-exp-receipt-indicator {
          cursor: pointer;
        }
        button.jfn-exp-receipt-indicator:focus-visible {
          outline: 3px solid #005fcc;
          outline-offset: 3px;
          border-radius: 7px;
        }
        .jfn-exp-receipt-paper {
          position: relative;
          width: 52px;
          height: 70px;
          min-width: 52px;
          min-height: 70px;
          max-width: 52px;
          max-height: 70px;
          border: 3px solid #5b6268;
          border-radius: 4px;
          background: #f7f7f7;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 5px;
          box-sizing: border-box;
          transition: transform 120ms ease, filter 120ms ease;
          overflow: hidden;
        }
        button.jfn-exp-receipt-indicator:hover .jfn-exp-receipt-paper {
          transform: translateY(-1px);
          filter: brightness(1.03);
        }
        .jfn-exp-receipt-indicator--image .jfn-exp-receipt-paper,
        .jfn-exp-receipt-indicator--pdf .jfn-exp-receipt-paper,
        .jfn-exp-receipt-indicator--attachment .jfn-exp-receipt-paper {
          border-color: #199b31;
          box-shadow: 0 0 0 3px #39ff14, 0 2px 6px rgba(0, 0, 0, 0.35);
        }
        .jfn-exp-receipt-word {
          color: #111;
          font-size: 9px;
          font-weight: 900;
          line-height: 1;
        }
        .jfn-exp-receipt-line {
          display: block;
          width: 64%;
          height: 3px;
          background: #9ca3a8;
        }
        .jfn-exp-receipt-line--short {
          width: 45%;
        }
        .jfn-exp-receipt-indicator--pdf .jfn-exp-receipt-paper::after,
        .jfn-exp-receipt-indicator--attachment .jfn-exp-receipt-paper::after {
          content: '';
          position: absolute;
          top: -3px;
          right: -3px;
          width: 35%;
          height: 35%;
          border-left: 3px solid #5b6268;
          border-bottom: 3px solid #5b6268;
          background: #dfe3e6;
          transform: skew(-8deg, -8deg);
          box-sizing: border-box;
        }
        .jfn-exp-pdf-word {
          width: 112%;
          padding: 4px 0;
          background: #e31b23;
          color: white;
          border: 2px solid #a80000;
          font-size: 13px;
          font-weight: 900;
          line-height: 1;
          text-align: center;
          z-index: 1;
          box-sizing: border-box;
        }
        .jfn-exp-attachment-mark {
          font-size: 24px;
          line-height: 1;
          z-index: 1;
        }
        .jfn-exp-receipt-indicator--none .jfn-exp-receipt-paper {
          border-color: #8b9298;
          background: #aeb4b9;
        }
        .jfn-exp-no-receipt-face {
          color: #e31b23;
          font-size: 30px;
          font-weight: 900;
          line-height: 1;
          text-shadow: none;
        }
        @media (max-width: 1023px) {
          .jfn-expense-list-row {
            --receipt-indicator-size: 46px;
            grid-template-columns: 42px minmax(0, 1fr);
            gap: 6px;
          }
          .jfn-exp-receipt-indicator {
            height: 78px;
            transform: translateX(-2px);
          }
          .jfn-exp-receipt-paper {
            border-width: 2px;
            gap: 3px;
          }
          button.jfn-exp-receipt-indicator {
            -webkit-appearance: none;
            appearance: none;
          }
          .jfn-exp-receipt-indicator .jfn-exp-receipt-paper {
            width: 50px;
            height: 78px;
            min-width: 50px;
            min-height: 78px;
            max-width: 50px;
            max-height: 78px;
            flex: 0 0 auto;
          }
          .jfn-expense-vendor-name {
            font-size: 16px !important;
          }
          .jfn-exp-receipt-indicator--image .jfn-exp-receipt-paper,
          .jfn-exp-receipt-indicator--pdf .jfn-exp-receipt-paper,
          .jfn-exp-receipt-indicator--attachment .jfn-exp-receipt-paper {
            box-shadow: 0 0 0 2px #39ff14, 0 2px 5px rgba(0, 0, 0, 0.3);
          }
          .jfn-exp-receipt-word {
            font-size: 7px;
          }
          .jfn-exp-receipt-line {
            height: 2px;
          }
          .jfn-exp-pdf-word {
            font-size: 10px;
            padding: 3px 0;
          }
          .jfn-exp-attachment-mark {
            font-size: 18px;
          }
          .jfn-exp-no-receipt-face {
            font-size: 24px;
          }
        }
        @media (max-width: 340px) {
          .jfn-expense-list-row {
            --receipt-indicator-size: 40px;
            grid-template-columns: 34px minmax(0, 1fr);
            gap: 6px;
          }
          .jfn-exp-receipt-indicator {
            height: 68px;
          }
          .jfn-exp-receipt-word {
            font-size: 6px;
          }
          .jfn-exp-receipt-indicator .jfn-exp-receipt-paper {
            width: 44px;
            height: 68px;
            min-width: 44px;
            min-height: 68px;
            max-width: 44px;
            max-height: 68px;
          }
          .jfn-exp-no-receipt-face {
            font-size: 21px;
          }
        }
      `}</style>
      {loading ? (
        <div style={{ fontWeight: 900, padding: '10px' }}>Loading expenses...</div>
      ) : null}

      {!loading && (!expenses || expenses.length === 0) ? (
        <div style={{ fontWeight: 900, padding: '10px' }}>No expenses found.</div>
      ) : null}

      {(expenses || []).map((exp) => {
        const vendor = String(exp.vendor || '').trim() || 'EXPENSE';
        const date = String(exp.date || '').slice(0, 10);
        const amount = exp.amount ?? 0;
        const receiptKind = getReceiptKind(exp);

        return (
          <div key={exp.id} className="jfn-expense-list-row">
            <ReceiptStatusGraphic
              kind={receiptKind}
              onOpen={() => openAttachment(exp)}
            />

            <div className="jfn-expense-list-card" style={cardStyle}>
              <div style={leftStyle}>
                <div className="jfn-expense-vendor-name" style={vendorStyle}>{vendor}</div>
                <div style={metaStyle}>
                  {money(amount)} • {toDisplayDate(date)}
                </div>
              </div>

              {isMobile ? (
                <div style={{ display: 'grid', gap: '6px', width: '100%' }}>
                  <div style={mobileActionRow}>
                    <button className="jfn-exp-btn" style={mobileButton(styles.btnLight)} onClick={() => viewExpense(exp)} type="button">
                      VIEW
                    </button>
                    <button className="jfn-exp-btn" style={mobileButton(styles.btnLight)} onClick={() => editExpense(exp)} type="button">
                      EDIT
                    </button>
                    <button className="jfn-exp-btn" style={mobileButton(styles.btnDanger)} onClick={() => deleteExpense(exp.id)} type="button">
                      DEL
                    </button>
                  </div>
                </div>
              ) : (
                <div style={btnRow}>
                  <button className="jfn-exp-btn" style={styles.btnLight} onClick={() => viewExpense(exp)} type="button">
                    VIEW
                  </button>
                  <button className="jfn-exp-btn" style={styles.btnLight} onClick={() => editExpense(exp)} type="button">
                    EDIT
                  </button>
                  <button className="jfn-exp-btn" style={styles.btnDanger} onClick={() => deleteExpense(exp.id)} type="button">
                    DELETE
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ExpensesList;
